import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import SEO from "components/SEO.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";
import { SectionHeading } from "components/misc/Headings";
import complianceImg from "assets/complianceCheckList_img.png";
import complianceImg1 from "assets/complianceCheckList_img1.png";
import complianceChecklistOverview from "assets/POSH_Act_Compliance_Checklist.png";

const HeadingRow = tw.div`flex`;
const Heading = tw(SectionHeading)`text-gray-900 mb-10`;

const IntroSection = tw.div`mb-12`;
const IntroImage = tw.img`w-full max-w-2xl mx-auto rounded-lg shadow-lg mb-8`;
const IntroText = tw.p`text-lg text-gray-700 leading-relaxed mb-6`;

const MythSection = tw.div`mb-12 bg-gray-100 rounded-lg p-8`;
const MythTitle = tw.h2`text-2xl font-bold text-red-600 mb-4 flex items-center`;
const RealityTitle = tw.h2`text-2xl font-bold text-green-600 mb-4 flex items-center`;
const MythIcon = tw.span`mr-3 text-3xl`;
const MythContent = tw.div`text-gray-700 leading-relaxed`;
const MythImage = tw.img`w-full max-w-md mx-auto rounded-lg shadow-md my-6`;

const ConclusionSection = styled.div`
  ${tw`mt-16 p-8 rounded-lg`}
  background-color: #fcc47eff;
`;
const ConclusionTitle = tw.h2`text-3xl font-bold text-black mb-6`;
const ConclusionText = tw.p`text-lg text-black leading-relaxed mb-4`;

const ArticleSection = tw.div`mb-12`;
const SectionTitle = styled.h2`
  ${tw`text-3xl font-bold mb-6`}
  color: #833C0B;
`;
const SectionContent = tw.div`text-gray-700 leading-relaxed`;
const HighlightBox = styled.div`
  ${tw` p-6 my-6`}
  background-color: #fffaf5ff;
`;
const ComplianceList = styled.ul`
  ${tw`ml-6`}
  li:not(:last-child) {
    margin-bottom: 0.75rem;
  }
`;
const ComplianceItem = tw.li`flex items-start`;
const BulletPoint = tw.span`text-black font-bold mr-3`;
const FlexContainer = styled.div`
  ${tw`flex items-center mb-8`}
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;
const ImageContainer = styled.div`
  ${tw`flex-shrink-0`}
  
  @media (max-width: 768px) {
    width: 100%;
    order: 1;
  }
`;
const ContentContainer = styled.div`
  ${tw`flex-1`}
  
  @media (max-width: 768px) {
    width: 100%;
    order: 2;
  }
`;
const FlexContainerReverse = styled.div`
  ${tw`flex items-center mb-8`}
  gap: 2rem;
  flex-direction: row-reverse;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
  
  ${ImageContainer} {
    @media (max-width: 768px) {
      order: 1;
    }
  }
  
  ${ContentContainer} {
    @media (max-width: 768px) {
      order: 2;
    }
  }
`;
const OverviewImage = tw.img`w-full max-w-md mx-auto rounded-lg shadow-md my-8`;
const PenaltyBox = styled.div`
  ${tw`p-6 my-6`}
  background-color: #fffaf5ff;
`;
const BestPracticeBox = styled.div`
  ${tw`p-6 my-6`}
  background-color: #fffaf5ff;
`;

const Text = styled.div`
  ${tw`text-lg text-gray-800`}
  p {
    ${tw`mt-2 leading-loose`}
  }
  h1 {
    ${tw`text-3xl font-bold mt-10`}
  }
  h2 {
    ${tw`text-2xl font-bold mt-8`}
  }
  h3 {
    ${tw`text-xl font-bold mt-6`}
  }
  ul {
    ${tw`list-disc list-inside ml-6`}
    li {
      ${tw`mb-3 text-gray-700`}
      p {
        ${tw`mt-0 inline leading-normal`}
      }
    }
  }
  strong {
    ${tw`font-semibold text-gray-900`}
  }
`;
export default ({ headingText = "POSH Act: Compliance Checklist for Indian Organisations in 2025" }) => {
    return (
        <AnimationRevealPage>
            <SEO
                title="POSH Act Compliance Checklist 2025 - SEQUILERN"
                description="Complete compliance checklist for POSH Act 2013. Learn about internal committees, policies, complaint processes, and best practices for Indian organizations in 2025."
                keywords="POSH Act, compliance checklist, sexual harassment, workplace safety, internal committee, POSH compliance 2025"
                url="https://www.sequilern.com/posh-compliance-checklist"
            />
            <Header />
            <Container>
                <ContentWithPaddingXl>
                    <HeadingRow>
                        <Heading>{headingText}</Heading>
                    </HeadingRow>

                    {/* Introduction Section */}
                    <IntroSection>
                        <IntroText>
                            Workplace equality in India has been shaped by landmark Supreme Court rulings—
                            <strong> Vishaka (1997)</strong> and <strong>Medha Kotwal Lele (2012)</strong>—which affirmed women's rights to
                            dignity, equality, and safe employment under the Constitution. These rulings led to the
                            POSH Act, 2013, which in 2025 remains both a legal mandate and a cultural benchmark.
                        </IntroText>
                    </IntroSection>

                    {/* Policy & Awareness Section */}
                    <ArticleSection>
                        <SectionTitle>1. Policy & Awareness</SectionTitle>
                        <HighlightBox>
                            <SectionContent>
                                <ComplianceList>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Adopt a clear anti-sexual harassment policy.</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Display it prominently at the workplace (Sec. 19).</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Conduct awareness sessions and training for employees, vendors, and visitors.</span>
                                    </ComplianceItem>
                                </ComplianceList>
                            </SectionContent>
                        </HighlightBox>
                    </ArticleSection>

                    {/* Internal Committee Section */}
                    <ArticleSection>
                        <SectionTitle>2. Internal Committee (IC)</SectionTitle>
                        <HighlightBox>
                            <SectionContent>
                                <ComplianceList>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Mandatory in every office/branch with 10+ employees (Sec. 4).</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Chaired by a senior woman employee.</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Must include at least 50% women members and one external NGO/legal expert.</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Designed to ensure credibility and trust.</span>
                                    </ComplianceItem>
                                </ComplianceList>
                            </SectionContent>
                        </HighlightBox>
                    </ArticleSection>

                    {/* Complaint & Inquiry Process Section with Image */}
                    <ArticleSection>
                        <SectionTitle>3. Complaint & Inquiry Process</SectionTitle>
                        <FlexContainer>
                            <ContentContainer>
                                <HighlightBox>
                                    <SectionContent>
                                        <ComplianceList>
                                            <ComplianceItem>
                                                <BulletPoint>•</BulletPoint>
                                                <span>Complaint to be filed within 3 months, extendable for valid reasons (Sec. 9).</span>
                                            </ComplianceItem>
                                            <ComplianceItem>
                                                <BulletPoint>•</BulletPoint>
                                                <span>IC must complete inquiry within 90 days and submit its report in 10 days (Sec. 11).</span>
                                            </ComplianceItem>
                                            <ComplianceItem>
                                                <BulletPoint>•</BulletPoint>
                                                <span>Conciliation allowed only at the complainant's request; monetary settlements prohibited.</span>
                                            </ComplianceItem>
                                        </ComplianceList>
                                    </SectionContent>
                                </HighlightBox>
                            </ContentContainer>
                            <ImageContainer>
                                <MythImage src={complianceImg} alt="POSH Complaint Process" />
                            </ImageContainer>
                        </FlexContainer>
                    </ArticleSection>

                    {/* Employer's Duties Section */}
                    <ArticleSection>
                        <SectionTitle>4. Employer's Duties</SectionTitle>
                        <HighlightBox>
                            <SectionContent>
                                <ComplianceList>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Ensure a safe work environment.</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Support the IC in securing witnesses/evidence.</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Treat sexual harassment as misconduct under service rules (Sec. 19).</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Report annual case statistics in reports or to the District Officer (Sec. 22).</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>•</BulletPoint>
                                        <span>Maintain strict confidentiality (Sec. 16–17).</span>
                                    </ComplianceItem>
                                </ComplianceList>
                            </SectionContent>
                        </HighlightBox>
                    </ArticleSection>

                    {/* Penalties Section */}
                    <ArticleSection>
                        <SectionTitle>5. Penalties for Non-Compliance</SectionTitle>
                        <PenaltyBox>
                            <SectionContent>
                                <ComplianceList>
                                    <ComplianceItem>
                                        <BulletPoint>⚠️</BulletPoint>
                                        <span>Fine up to <strong>₹50,000</strong> for failing to form an IC or follow provisions (Sec. 26).</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>⚠️</BulletPoint>
                                        <span>Repeat violations → double fines + possible license cancellation.</span>
                                    </ComplianceItem>
                                    <ComplianceItem>
                                        <BulletPoint>⚠️</BulletPoint>
                                        <span>Non-compliance also risks reputational harm and legal scrutiny.</span>
                                    </ComplianceItem>
                                </ComplianceList>
                            </SectionContent>
                        </PenaltyBox>
                    </ArticleSection>

                    {/* Best Practices Section with Image */}
                    <ArticleSection>
                        <SectionTitle>6. Beyond Compliance: Best Practices (2025)</SectionTitle>
                        <FlexContainerReverse>
                            <ContentContainer>
                                <IntroText>Forward-looking organisations now adopt:</IntroText>
                                <BestPracticeBox>
                                    <SectionContent>
                                        <ComplianceList>
                                            <ComplianceItem>
                                                <BulletPoint>✓</BulletPoint>
                                                <span>Digital POSH training in regional languages.</span>
                                            </ComplianceItem>
                                            <ComplianceItem>
                                                <BulletPoint>✓</BulletPoint>
                                                <span>Anonymous reporting platforms.</span>
                                            </ComplianceItem>
                                            <ComplianceItem>
                                                <BulletPoint>✓</BulletPoint>
                                                <span>Third-party audits of POSH processes.</span>
                                            </ComplianceItem>
                                        </ComplianceList>
                                    </SectionContent>
                                </BestPracticeBox>
                            </ContentContainer>
                            <ImageContainer>
                                <MythImage src={complianceImg1} alt="POSH Best Practices 2025" />
                            </ImageContainer>
                        </FlexContainerReverse>
                    </ArticleSection>

                    {/* Conclusion Section */}
                    <ConclusionSection>
                        <ConclusionTitle>The Takeaway</ConclusionTitle>
                        <ConclusionText>
                            <strong>Compliance is the starting point; culture is the real test.</strong> As Vishaka highlighted, workplaces
                            must ensure women can work with dignity and without fear. For organisations in 2025, POSH
                            compliance is not just a legal requirement but a measure of corporate credibility and integrity.
                        </ConclusionText>
                    </ConclusionSection>

                    {/* At-a-Glance Overview */}
                    <ArticleSection style={{ marginTop: '4rem' }}>
                        <SectionTitle>At a Glance</SectionTitle>
                        <OverviewImage src={complianceChecklistOverview} alt="POSH Act Compliance Checklist Overview" />
                    </ArticleSection>
                </ContentWithPaddingXl>
            </Container>
            <Footer />
        </AnimationRevealPage>
    );
};

